import React from "react";

import { VehicleStore, FilterStore, ConfigurationStore } from "stores";

import Box from "@mui/material/Box";

import { TVehicle } from "types";
import Header from "./Header";
import ModelleCard from "./Card";
import { EmptySearch } from "../";
import { groupVehicles } from "../helper";

import { useSmartNavigate } from "helper/SmartLink";

interface IModelleCatalog {
  vehicles: TVehicle[];
  sortOrder?: string;
  dispatchFilter?: any;
  financeFilter?: any;
  favouritesMarke?: number[];
  favouritesModell?: number[];
}

export const ModelleCatalog: React.FC<IModelleCatalog> = ({
  vehicles,
  sortOrder = "preis_asc",
  dispatchFilter = () => null,
  favouritesMarke = [],
  favouritesModell = [],
  financeFilter = {},
}): JSX.Element | null => {
  const navigate = useSmartNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!vehicles) return null;
  if (vehicles.length === 0) return <EmptySearch />;

  const sorter: any = {
    empfohlen: (a: any, b: any) => {
      if (favouritesModell) {
        if (favouritesModell.includes(a[1].modell.id)) {
          if (favouritesModell.includes(b[1].modell.id)) {
            return b[1]["vorrat"] < a[1]["vorrat"] ? -1 : 1;
          }
          return -1;
        }
        if (favouritesModell.includes(b[1].modell.id)) {
          return 1;
        }
      }
      if (favouritesMarke) {
        if (favouritesMarke.includes(a[1].modell?.marke.id)) {
          if (favouritesMarke.includes(b[1].modell?.marke.id)) {
            return b[1]["vorrat"] < a[1]["vorrat"] ? -1 : 1;
          }
          return -1;
        }
        if (favouritesMarke.includes(b[1].modell?.marke.id)) {
          return 1;
        }
      }
      return b[1]["vorrat"] < a[1]["vorrat"] ? -1 : 1;
    },

    preis_asc: (a: any, b: any) => {
      return b[1]["preis"] > a[1]["preis"] ? -1 : 1;
    },
    preis_desc: (a: any, b: any) => {
      return b[1]["preis"] < a[1]["preis"] ? -1 : 1;
    },
    rate_asc: (a: any, b: any) => {
      if (!a[1]["rate"]) return 1;
      if (!b[1]["rate"]) return -1;
      return b[1]["rate"] > a[1]["rate"] ? -1 : 1;
    },
    rate_desc: (a: any, b: any) => {
      if (!a[1]["rate"]) return 1;
      if (!b[1]["rate"]) return -1;
      return b[1]["rate"] < a[1]["rate"] ? -1 : 1;
    },
    km_asc: (a: any, b: any) => {
      if (!a[1]["km_min"]) return 1;
      if (!b[1]["km_min"]) return -1;
      return b[1]["km_min"] > a[1]["km_min"] ? -1 : 1;
    },
    km_desc: (a: any, b: any) => {
      if (!a[1]["km_max"]) return 1;
      if (!b[1]["km_max"]) return -1;
      return b[1]["km_max"] < a[1]["km_max"] ? -1 : 1;
    },
    erstzulassung_asc: (a: any, b: any) => {
      if (!a[1]["erstzulassung_min"]) return 1;
      if (!b[1]["erstzulassung_min"]) return -1;
      return b[1]["erstzulassung_min"] > a[1]["erstzulassung_min"] ? -1 : 1;
    },
    erstzulassung_desc: (a: any, b: any) => {
      if (!a[1]["erstzulassung_max"]) return 1;
      if (!b[1]["erstzulassung_max"]) return -1;
      return b[1]["erstzulassung_max"] < a[1]["erstzulassung_max"] ? -1 : 1;
    },
  };

  const groups = groupVehicles(vehicles).sort(sorter[sortOrder]);
  const setModell = (id: any) => {
    // const payload = {
    //   type: "UPDATE_FILTER",
    //   payload: { modell: [id] },
    // };
    // dispatchFilter(payload);
    navigate("/catalog/mo/" + id);
  };

  const setModellGroup = (id: any) => {
    // const payload = {
    //   type: "UPDATE_FILTER",
    //   payload: { modell_gruppe: [id] },
    // };
    // dispatchFilter(payload);
    navigate("/catalog/mg/" + id);
  };

  const detail = (id: any) => {
    navigate("/catalog/dt/" + id);
  };

  const vatExcluded = financeFilter?.customerType === "business" || false;
  return (
    <Box
      sx={{
        display: "grid",
        columnGap: { xs: 1, sm: 2, md: 3, lg: 4 },
        rowGap: { xs: 1, sm: 2, md: 3, lg: 4 },
        gridTemplateColumns: {
          lg: "repeat(3, 1fr)",
          sm: "repeat(2, 1fr)",
          xs: "repeat(1, 1fr)",
        },
      }}
    >
      {groups.map(([ndx, group]: [string, any]) => {
        const Card = group.groupType === "gruppe" ? ModelleCard : ModelleCard;
        const handler =
          group.vorrat === 1
            ? detail
            : group.groupType === "gruppe"
              ? setModellGroup
              : setModell;
        const id = group.vorrat === 1 ? group.id : group.groupId;

        return (
          <Card
            key={id}
            data={group}
            onClickHandler={() => handler(id)}
            vatExcluded={vatExcluded}
          />
        );
      })}
    </Box>
  );
};

interface IPipeline {}

const Pipeline: React.FC<IPipeline> = (): JSX.Element | null => {
  const { state: vehicles } = React.useContext(VehicleStore);
  const configuration = React.useContext(ConfigurationStore);

  const {
    state: {
      order: sortOrder,
      filter: { finance: financeFilter },
    },
    dispatch: dispatchFilter,
  } = React.useContext(FilterStore);

  const favouritesMarke = configuration?.filter?.favourites?.marke || undefined;
  const favouritesModell =
    configuration?.filter?.favourites?.modell || undefined;

  return (
    <>
      <Header />
      <ModelleCatalog
        {...{
          vehicles,
          sortOrder,
          dispatchFilter,
          favouritesMarke,
          favouritesModell,
          financeFilter,
        }}
      />
    </>
  );
};

export default Pipeline;
